/**
 * Cloudflare Access Authentication Service
 * Handles JWT tokens and user authentication
 */

// Cloudflare Access configuration
// const AUTH_CONFIG = {
//   // Your Cloudflare Access team domain
//   teamDomain: process.env.REACT_APP_CF_ACCESS_TEAM_DOMAIN || 'lsas.cloudflareaccess.com',
//   // Your Access application AUD tag
//   audienceTag: process.env.REACT_APP_CF_ACCESS_AUD || '',
// };

/**
 * Get current user information from Cloudflare Access
 * @returns {Promise<Object|null>} User object or null if not authenticated
 */
export const getCurrentUser = async () => {
  try {
    // Check if we're on a preview domain where Access endpoints don't exist
    const isPreviewDomain = window.location.hostname.includes('.pages.dev');
    
    if (isPreviewDomain) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Preview domain detected - Access endpoints not available');
      }
      return null; // This will trigger the "not authenticated" flow
    }

    // In production, check for Cloudflare Access headers
    const response = await fetch('/cdn-cgi/access/get-identity', {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const identity = await response.json();
    
    // Clear any redirect attempt flag since we're now authenticated
    sessionStorage.removeItem('cf-access-redirect-attempted');
    
    return {
      email: identity.email,
      name: identity.name || identity.email,
      role: 'admin', // For now, assume all authenticated users are admin
      authenticated: true,
      identity,
    };
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

/**
 * Get user role from admin API (TEMPORARILY DISABLED)
 * @param {string} email - User email
 * @returns {Promise<string>} User role
 */
// const getUserRole = async (email) => {
//   try {
//     const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/user/role`, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ email }),
//       credentials: 'include',
//     });

//     if (response.ok) {
//       const data = await response.json();
//       return data.role || 'client';
//     }
//   } catch (error) {
//     console.error('Failed to get user role:', error);
//   }
  
//   return 'client';
// };

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} Authentication status
 */
export const isAuthenticated = async () => {
  const user = await getCurrentUser();
  return user && user.authenticated;
};

/**
 * Check if user has admin role
 * @returns {Promise<boolean>} Admin status
 */
export const isAdmin = async () => {
  const user = await getCurrentUser();
  return user && user.role === 'admin';
};

/**
 * Redirect to Cloudflare Access login
 */
export const redirectToLogin = () => {
  // Check if we're on a preview domain where Access endpoints don't exist
  const isPreviewDomain = window.location.hostname.includes('.pages.dev');
  
  if (isPreviewDomain) {
    if (process.env.NODE_ENV === 'development') {
      alert('Cloudflare Access is not available on preview domains.\n\nTo test authentication:\n1. Deploy to lonestaraerialsolutions.com\n2. Configure Access for your custom domain\n3. Test at https://lonestaraerialsolutions.com/admin');
    }
    return;
  }

  // Check if we've already tried to redirect to prevent infinite loops
  const hasTriedRedirect = sessionStorage.getItem('cf-access-redirect-attempted');
  
  if (hasTriedRedirect) {
    if (process.env.NODE_ENV === 'development') {
      alert('Authentication Error: Cloudflare Access is not protecting this path.\n\nPlease check your Access application configuration:\n1. Verify domain matches: lonestaraerialsolutions.com\n2. Verify path matches: /admin/*\n3. Ensure application is enabled');
    }
    return;
  }

  // Mark that we've attempted a redirect
  sessionStorage.setItem('cf-access-redirect-attempted', 'true');
  
  // For Cloudflare Access, we need to trigger authentication by accessing a protected resource
  // The Access application will automatically redirect to login when needed
  
  // Force a page reload to trigger Access protection
  // This will cause Cloudflare to intercept the request and redirect to login
  window.location.reload();
};

/**
 * Logout from Cloudflare Access
 */
export const logout = async () => {
  try {
    // First, log the logout action
    const response = await fetch('/admin/api/auth/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      // Redirect to Cloudflare Access logout
      window.location.href = data.redirect_url || '/cdn-cgi/access/logout';
    } else {
      // If logging fails, still redirect to logout
      window.location.href = '/cdn-cgi/access/logout';
    }
  } catch (error) {
    console.error('Failed to log logout:', error);
    // If logging fails, still redirect to logout
    window.location.href = '/cdn-cgi/access/logout';
  }
};

/**
 * Get authentication headers for API requests
 * @returns {Promise<Object>} Headers object with Access token
 */
export const getAuthHeaders = async () => {
  // Since the API is now under /admin/api, we can use the same Access session
  // No need for separate token requests - just use the existing session
  
  return {
    'Content-Type': 'application/json',
    // The CF-Access headers will be automatically included by the browser
    // since we're making requests to the same domain and Access application
  };
};
